import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import React from "react";
/**
 * Renders the custom next arrow for the carousel.
 *
 * @param {Object} props - The props passed to the arrow component.
 * @param {string} props.className - The class name of the arrow element.
 * @param {Object} props.customStyle - The inline style for the arrow element.
 * @param {Function} props.onClick - The function to be called when the arrow is clicked.
 * @param {string} props.right - The right position of the arrow element.
 * @param {number} props.width - The width of the arrow image.
 * @param {number} props.height - The height of the arrow image.
 *
 * @returns {JSX.Element} The rendered next arrow component.
 */
export function SlickNextArrow({ className, customStyle, onClick, right = "10px", width = 24, height = 24 }) {
  return (
    <div
      className={`${className} custom-arrow`}
      style={{ ...customStyle, display: "block", right: right, zIndex: 9 }}
      onClick={onClick}
    >
      <CustomImage src="/icons/arrow-right.svg" alt="right arrow" width={width} height={height} sizes="10vw" />
    </div>
  );
}
SlickNextArrow.propTypes = {
  className: PropTypes.string,
  customStyle: PropTypes.object,
  onClick: PropTypes.func,
  right: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};
/**
 * Renders the custom previous arrow for the carousel.
 *
 * @param {Object} props - The props passed to the arrow component.
 * @param {string} props.className - The class name of the arrow element.
 * @param {Object} props.customStyle - The inline style for the arrow element.
 * @param {Function} props.onClick - The function to be called when the arrow is clicked.
 * @param {string} props.left - The left position of the arrow element.
 * @param {number} props.width - The width of the arrow image.
 * @param {number} props.height - The height of the arrow image.
 *
 * @returns {JSX.Element} The rendered previous arrow component.
 */
export function SlickPrevArrow({ className, customStyle, onClick, left = "10px", width = 24, height = 24 }) {
  return (
    <div
      className={`${className} custom-arrow`}
      style={{ ...customStyle, display: "block", left: left, zIndex: 9 }}
      onClick={onClick}
    >
      <CustomImage src="/icons/arrow-left.svg" alt="left arrow" width={width} height={height} sizes="10vw" />
    </div>
  );
}
SlickPrevArrow.propTypes = {
  className: PropTypes.string,
  customStyle: PropTypes.object,
  onClick: PropTypes.func,
  left: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};
/**
 * Renders custom dots for the carousel.
 *
 * @param {Array} dots - The list of dot elements that will be rendered.
 *
 * @returns {JSX.Element} The rendered dot container element.
 */
export function appendDots(dots, activeSlide, totalSlides, slidesToShow, infinite, customClass = "") {
  const roundedActiveSlide = Math.round(activeSlide);
  return (
    <div>
      <ul className={`customDots ${customClass}`}>
        {dots.map((dot, index) => {
          let dotClass = "";

          const lastVisibleSlide = totalSlides - slidesToShow;

          if (index === roundedActiveSlide) {
            dotClass = "active";
          } else if (index === roundedActiveSlide + 1 || index === roundedActiveSlide - 1) {
            dotClass = "next";
          } else if (index === roundedActiveSlide + 2 || index === roundedActiveSlide - 2) {
            dotClass = "next-smaller";
          } else if (index === roundedActiveSlide + 3 || index === roundedActiveSlide - 3) {
            dotClass = "next-smallest";
          }

          // Adjust for when `activeSlide` is near the end
          if (!infinite && roundedActiveSlide >= lastVisibleSlide) {
            if (index >= lastVisibleSlide && index <= roundedActiveSlide) {
              dotClass = "active";
            } else if (index >= lastVisibleSlide - 1) {
              dotClass = "next";
            } else if (index >= lastVisibleSlide - 2) {
              dotClass = "next-smaller";
            } else if (index >= lastVisibleSlide - 3) {
              dotClass = "next-smallest";
            }
          }

          return React.cloneElement(dot, {
            className: `${dot.props.className} ${dotClass}`,
          });
        })}
      </ul>
    </div>
  );
}

/**
 * Custom paging logic for the carousel.
 * @param {number} activeSlide - The index of the currently active slide.
 * @param {Array} data - The array of carousel data used for determining total slides.
 * @returns {Function} A function that returns the custom dot for the specified slide index.
 */
export function customPaging(activeSlide, data) {
  /**
   * Custom paging logic for the carousel. Renders a custom dot that shows the current slide number and total slides.
   * @param {number} i - The index of the current slide being processed.
   * @returns {JSX.Element} A JSX element that renders the custom dot with the current slide number and total slides.
   */
  const paging = (i) => {
    const roundedActiveSlide = Math.round(activeSlide);

    return <div className="customDot">{i === roundedActiveSlide ? `${i + 1} / ${data.length}` : ""}</div>;
  };

  // Adding a display name to the function for debugging
  paging.displayName = "CustomPaging";

  return paging;
}
